import React, { useEffect, useRef } from 'react';
import {
  CheckCircleOutlineIcon,
  ErrorOutlineIcon,
  Field,
  Input,
  Spinner,
} from '@billda/design-system';
import { useFormContext, useWatch, useFormState } from 'react-hook-form';
import { useCheckSlugUniqueness } from '../../../../hooks/useCheckSlugUniqueness';
import { SiteImportFormData } from '../../../../schemas/siteImportSchema';

const SiteNameCodeFields: React.FC = () => {
  const form = useFormContext<SiteImportFormData>();
  const { control, setValue } = form;
  const { errors } = useFormState({ control });

  const name = useWatch({ control, name: 'name' }) ?? '';
  const slug = useWatch({ control, name: 'slug' }) ?? '';

  const { checkSlug, isChecking: slugChecking } = useCheckSlugUniqueness(form);
  const slugManuallyEdited = useRef(false);

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setValue('name', value, { shouldValidate: true, shouldDirty: true });
    if (!slugManuallyEdited.current) {
      const derivedSlug = value
        .replace(/[^a-zA-Z0-9]/g, '')
        .toUpperCase()
        .slice(0, 4);
      setValue('slug', derivedSlug, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  };

  const handleSlugChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '')
      .slice(0, 4);
    slugManuallyEdited.current = value.length > 0;
    setValue('slug', value, { shouldValidate: true, shouldDirty: true });
  };

  useEffect(() => {
    if (slug.length === 4) {
      checkSlug(slug);
    }
  }, [slug, checkSlug]);

  const getSlugAdornment = () => {
    if (slug.length !== 4) {
      return null;
    }
    if (slugChecking) {
      return <Spinner size={20} />;
    }
    if (errors.slug) {
      return (
        <ErrorOutlineIcon
          className="size-5 text-content-error"
          aria-hidden="true"
        />
      );
    }
    if (!slugChecking) {
      return (
        <CheckCircleOutlineIcon
          className="size-5 text-content-success"
          aria-hidden="true"
        />
      );
    }
    return null;
  };

  const getSlugHelperText = () => {
    if (slug.length === 0) {
      return 'Required - 4 uppercase letters/numbers (e.g., Brough could be BRGH)';
    }
    if (errors.slug) {
      return errors.slug.message?.toString();
    }
    if (slugChecking) {
      return 'Checking availability...';
    }
    if (slug.length === 4 && !slugChecking && !errors.slug) {
      return 'This slug is available';
    }
    return '4 uppercase letters/numbers (e.g., Brough could be BRGH)';
  };

  return (
    <>
      <Field
        label="Site Name"
        required
        error={errors.name?.message?.toString()}
        hint={errors.name ? undefined : 'Must be at least 3 characters long'}
      >
        <Input
          value={name}
          onChange={handleNameChange}
          invalid={Boolean(errors.name)}
          data-testid="site-name-input"
        />
      </Field>
      <Field
        label="Site Code"
        required
        error={errors.slug?.message?.toString()}
        hint={errors.slug ? undefined : getSlugHelperText()}
      >
        <Input
          value={slug}
          onChange={handleSlugChange}
          placeholder="X7K2"
          invalid={Boolean(errors.slug)}
          maxLength={4}
          className="uppercase"
          endAdornment={getSlugAdornment()}
          data-testid="site-slug-input"
        />
      </Field>
    </>
  );
};

export default SiteNameCodeFields;
